import { Select, SelectProps } from '@billy/design-system'
import { forwardRef, ReactElement, Ref, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { CountryIsoCode } from '../../../types/countryIsoCode'
import { CountryFlag } from '../../CountryFlag'
import { LocaleFormat } from './types/LocaleFormat'
import { getLanguageItems } from './utils/getLanguageItems'

interface LanguageDropdownProps extends Omit<SelectProps<LocaleFormat>, 'items' | 'onSelect' | 'prefix'> {
  languages?: LocaleFormat[]
  onSelect?: (value?: LocaleFormat) => void
}

export const LanguageSelect = forwardRef(
  (
    { selectedId, onSelect, languages, ...selectProps }: LanguageDropdownProps,
    ref: Ref<HTMLDivElement | null>,
  ): ReactElement => {
    const { t } = useTranslation()
    const languageItems = useMemo(() => getLanguageItems(t, languages), [t, languages])

    const selectedItem = useMemo(() => languageItems.find(({ id }) => selectedId === id), [selectedId, languageItems])

    const renderPrefix = useMemo(
      () => (selectedItem?.title ? <CountryFlag countryCode={selectedItem.title as CountryIsoCode} /> : null),
      [selectedItem],
    )

    const handleSelect = useCallback(
      (_id?: string, value?: LocaleFormat) => {
        onSelect?.(value)
      },
      [onSelect],
    )

    return (
      <Select
        {...selectProps}
        dropdownSize={selectProps.dropdownSize || 'fitTrigger'}
        items={languageItems}
        onSelect={handleSelect}
        prefix={renderPrefix}
        ref={ref}
        selectedId={selectedId}
      />
    )
  },
)
